import React from 'react';
import { useParams } from 'react-router-dom';
import { ApolloError } from '@apollo/client';
import { Card, Text } from '@perch-labs/shrike';
import { formatDate } from '@perch-labs/warbler';
import { routes as getRoutes, copyFor } from 'config';
import { Container } from 'styles';
import { ErrorModule } from 'components/errors';
import { LoadingModule } from 'components/loading';
import { StandardRow } from 'components/standard-row';
import { QueriedStatement } from 'pages/billing/billing-typings';
import { StyledCard, CardContent } from './latest-statement.style';
import { TotalDue } from '../total-due';
import { UtilityStatementSummary } from '../utility-statement-summary';
import { StatementBanner } from '../statement-banner';

const getCopy = copyFor('billing.dashboard.latestStatement');

interface Props {
  error?: ApolloError;
  loading: boolean;
  mostRecentStatement?: QueriedStatement;
}

export const LatestStatement = (props: Props) => {
  const { planId } = useParams<{ planId?: string }>();
  const { error, loading, mostRecentStatement } = props;
  if (loading) return <LoadingModule />;
  if (error) return <ErrorModule title={getCopy('title')} />;

  if (!mostRecentStatement) return null;

  const primaryDate =
    mostRecentStatement.utilityStatements[0]?.serviceEndDate ||
    mostRecentStatement.issuedAt;

  const { id, delayable, hostReportItems, utilityStatements } =
    mostRecentStatement;
  const routes = getRoutes({ planId, statementId: id });

  return (
    <StyledCard mobileVariant="flat">
      <Card.Header>
        <Container>
          <Text tag="h1" margin="0 0 4px" textStyle="heading800">
            {getCopy('title', {
              monthName: formatDate(primaryDate, {
                format: 'MMMM',
              }),
            })}
          </Text>
          <Text textStyle="paragraph400" color="secondary">
            {formatDate(primaryDate, {
              format: 'MMMM dd, yyyy',
            })}
          </Text>
        </Container>
      </Card.Header>
      <CardContent>
        <StatementBanner statement={mostRecentStatement} />
        <TotalDue
          margin="32px 0 0"
          total={mostRecentStatement.total}
          totalBeforeSolarSavings={mostRecentStatement.totalBeforeSolarSavings}
          headlineSolarSavingsRate={
            mostRecentStatement.lineItemSummary.headlineSolarSavingsRate
          }
        />
        {utilityStatements.slice(0, 1).map(us => (
          <UtilityStatementSummary
            key={us.id}
            margin="48px 0"
            gap="8px"
            textStyle="paragraph400"
            iconScale={1.75}
            periodLabel="servicePeriod"
            accountNumber={us.utilityAccount.accountNumber || '****'}
            serviceAddress={us.utilityAccount.displayServiceAddress}
            serviceEndDate={us.serviceEndDate}
            serviceStartDate={us.serviceStartDate}
          />
        ))}
        {utilityStatements.length > 1 && (
          <Text textStyle="paragraph400">
            {getCopy('moreStatements', {
              count: utilityStatements.length - 1,
            })}
          </Text>
        )}
        {hostReportItems.slice(0, 1).map(item => (
          <UtilityStatementSummary
            key={item.id}
            margin="48px 0 0"
            gap="8px"
            periodLabel="productionPeriod"
            accountNumber={item.utilityAccount?.accountNumber || '****'}
            serviceAddress={item.utilityAccount?.displayServiceAddress ?? ''}
            serviceEndDate={item.productionEndDate ?? item.dateReadingTo}
            serviceStartDate={item.productionStartDate ?? item.dateReadingFrom}
          />
        ))}
        {hostReportItems.length > 1 && (
          <Text textStyle="paragraph400" margin="16px 0 0">
            {getCopy('moreAccounts', {
              count: hostReportItems.length - 1,
            })}
          </Text>
        )}
      </CardContent>
      <Container margin="48px 0 0">
        {mostRecentStatement.invoiceUrl &&
        mostRecentStatement.status !== 'paid' ? (
          <StandardRow
            includesBuffer={true}
            bufferWidth="32px"
            iconLinkProps={{
              ariaLabel: getCopy('links.payInvoice'),
              href: mostRecentStatement.invoiceUrl,
            }}
            heading={getCopy('links.payInvoice')}
          />
        ) : null}
        <StandardRow
          includesBuffer={true}
          bufferWidth="32px"
          iconLinkProps={{
            ariaLabel: getCopy('links.fullStatement'),
            to: routes.statement,
          }}
          heading={getCopy('links.fullStatement')}
        />
        {delayable && (
          <StandardRow
            includesBuffer={true}
            bufferWidth="32px"
            heading={getCopy('links.delayPayment')}
            iconLinkProps={{
              to: routes.statementDelayPayment,
            }}
          />
        )}
        {['issued', 'payment_failed'].includes(mostRecentStatement.status) &&
          mostRecentStatement.collectAutomatically && (
            <StandardRow
              includesBuffer={true}
              bufferWidth="32px"
              heading={getCopy('links.updatePayment')}
              iconLinkProps={{
                to: routes.paymentSettings,
              }}
            />
          )}
      </Container>
    </StyledCard>
  );
};
